import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  AlertTriangle, Clock, CheckCircle2, RefreshCw, Zap, User, Wrench, Package, ArrowRight, Search
} from "lucide-react";

const REFRESH_S = 15;

const InspectionQueue = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | overdue | due | on-time
  const [countdown, setCountdown] = useState(REFRESH_S);
  const [q, setQ] = useState("");

  const fetchQueue = useCallback(() => {
    setLoading(true);
    api.get("/inspection-queue")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchQueue();
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { fetchQueue(); return REFRESH_S; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [fetchQueue]);

  const filteredJobs = (() => {
    let jobs = data?.jobs || [];
    if (filter === "overdue") jobs = jobs.filter((j) => j.status === "OVERDUE");
    else if (filter === "due") jobs = jobs.filter((j) => j.status === "DUE");
    else if (filter === "on-time") jobs = jobs.filter((j) => j.status === "ON-TIME");
    if (q.trim()) {
      const s = q.toLowerCase();
      jobs = jobs.filter((j) =>
        (j.job_id || "").toLowerCase().includes(s) ||
        (j.machine || "").toLowerCase().includes(s) ||
        (j.tool_no || "").toString().toLowerCase().includes(s) ||
        (j.part_name || "").toLowerCase().includes(s) ||
        (j.operator || "").toLowerCase().includes(s)
      );
    }
    return jobs;
  })();

  const inspect = (job) => navigate(`/inspection?job=${encodeURIComponent(job.job_id)}`);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2">
              <AlertTriangle size={22} className="text-red-600" /> Inspection Queue
            </h1>
            <p className="text-sm text-slate-500">All open jobs prioritized by inspection status.</p>
          </div>
          <div className="text-xs text-slate-400 inline-flex items-center gap-1">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Auto-refresh in {countdown}s
          </div>
        </div>

        {/* Counts */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <StatCard
            label="OVERDUE"
            value={data?.counts?.overdue || 0}
            color="bg-red-50 border-red-200 text-red-700"
            active={filter === "overdue"}
            onClick={() => setFilter("overdue")}
            icon={<AlertTriangle size={16} />}
          />
          <StatCard
            label="DUE"
            value={data?.counts?.due || 0}
            color="bg-amber-50 border-amber-200 text-amber-700"
            active={filter === "due"}
            onClick={() => setFilter("due")}
            icon={<Clock size={16} />}
          />
          <StatCard
            label="ON-TIME"
            value={data?.counts?.on_time || 0}
            color="bg-emerald-50 border-emerald-200 text-emerald-700"
            active={filter === "on-time"}
            onClick={() => setFilter("on-time")}
            icon={<CheckCircle2 size={16} />}
          />
          <StatCard
            label="ALL OPEN"
            value={data?.counts?.total || 0}
            color="bg-slate-50 border-slate-200 text-slate-700"
            active={filter === "all"}
            onClick={() => setFilter("all")}
            icon={<Zap size={16} />}
          />
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 mb-4 flex items-center gap-2">
          <Search size={16} className="text-slate-400" />
          <input
            data-testid="inspq-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search job, machine, tool, part, operator..."
            className="flex-1 text-sm outline-none"
          />
          {q && (
            <button onClick={() => setQ("")} className="text-xs font-bold text-slate-400 hover:text-slate-700">Clear</button>
          )}
        </div>

        {/* Rows */}
        {loading && !data ? (
          <div className="text-center text-slate-400 py-10">Loading...</div>
        ) : filteredJobs.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 text-center">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-2" />
            <p className="font-extrabold text-slate-700">All caught up</p>
            <p className="text-sm text-slate-400">No jobs match your current filter.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="inspq-list">
            {filteredJobs.map((j) => (
              <JobRow key={j.job_id} job={j} onInspect={() => inspect(j)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, active, onClick, icon }) => (
  <button
    data-testid={`inspq-tab-${label.toLowerCase()}`}
    onClick={onClick}
    className={`text-left rounded-2xl border-2 p-3 transition-all ${
      active ? `${color} ring-2 ring-current/20 shadow-lg` : "bg-white border-slate-200 text-slate-500 hover:border-slate-400"
    }`}
  >
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-extrabold uppercase tracking-widest">{label}</p>
      <div className="opacity-80">{icon}</div>
    </div>
    <p className="text-3xl font-extrabold mt-1">{value}</p>
  </button>
);

const JobRow = ({ job, onInspect }) => {
  const isOverdue = job.status === "OVERDUE";
  const isDue = job.status === "DUE";
  const isFirstPart = job.ever_inspected === false;
  const cardCls = isOverdue
    ? "bg-red-50 border-red-300 hover:border-red-500"
    : isDue
    ? "bg-amber-50 border-amber-300 hover:border-amber-500"
    : "bg-white border-slate-200 hover:border-slate-400";

  const badgeCls = isOverdue
    ? "bg-red-600 text-white"
    : isDue
    ? "bg-amber-500 text-white"
    : "bg-emerald-500 text-white";

  const label = isOverdue
    ? (isFirstPart ? "FIRST-PART DUE" : "OVERDUE")
    : job.status;

  const mins = job.mins_since_last;
  const minsTxt =
    mins == null ? "—" :
    mins < 60 ? `${Math.round(mins)} min` :
    `${(mins / 60).toFixed(1)} hrs`;

  return (
    <li
      data-testid={`inspq-row-${job.job_id}`}
      className={`rounded-2xl border-2 p-3 transition-all ${cardCls}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full ${badgeCls}`}>
            {label}
          </span>
          <div className="min-w-0">
            <p className="font-extrabold text-slate-900 text-sm inline-flex items-center gap-2">
              <Package size={13} className="text-slate-500" />
              {job.machine}
              <span className="text-slate-400 font-normal">·</span>
              <Wrench size={12} className="text-slate-500" />
              T{job.tool_no}
              <span className="text-slate-400 font-normal">·</span>
              <span className="text-slate-700 truncate max-w-xs">{job.part_name}</span>
            </p>
            <p className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
              <User size={11} className="text-slate-400" /> {job.operator}
              <span className="text-slate-300">·</span>
              <span className="font-mono">{job.job_id}</span>
              {job.interval_hours && (
                <>
                  <span className="text-slate-300">·</span>
                  <span>Interval {job.interval_hours}h</span>
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-[10px] font-bold text-slate-400 uppercase">
              {isFirstPart ? "Since start" : "Since last insp"}
            </p>
            <p className={`text-lg font-extrabold ${
              isOverdue ? "text-red-700" : isDue ? "text-amber-700" : "text-slate-700"
            }`}>
              {minsTxt}
            </p>
          </div>
          <button
            data-testid={`inspq-btn-${job.job_id}`}
            onClick={onInspect}
            className={`inline-flex items-center gap-1.5 text-sm font-extrabold text-white px-4 py-2 rounded-xl transition-all ${
              isOverdue
                ? "bg-red-600 hover:bg-red-700 shadow-lg shadow-red-200"
                : isDue
                ? "bg-amber-600 hover:bg-amber-700 shadow-md"
                : "bg-[#1A3560] hover:bg-[#152a4d]"
            }`}
          >
            Inspect Now <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </li>
  );
};

export default InspectionQueue;
